import React, { useState } from 'react';
import { useAuth } from '@/auth/useAuth';

export default function LoginPage() {
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleLogin() {
    setIsLoading(true);
    setError(null);
    try {
      await login();
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Sign-in failed. Please try again.';
      // Ignore user-cancelled popup
      if (!msg.toLowerCase().includes('cancelled') && !msg.toLowerCase().includes('user_cancelled')) {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-jm-navy to-jm-navy-dark px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header band */}
          <div className="bg-jm-navy px-8 py-8 flex flex-col items-center">
            <img
              src="https://jmtest.com/wp-content/uploads/2017/04/JM-Test-Systems-logo.jpg"
              alt="JM Test Systems"
              className="h-14 w-auto object-contain bg-white rounded-lg px-3 py-1 mb-5"
            />
            <h1 className="font-montserrat text-2xl font-bold text-white">
              Welcome to Atlas
            </h1>
            <p className="mt-1.5 text-sm text-white/70 font-work-sans text-center">
              Your employee hub for JM Test Systems resources
            </p>
          </div>

          {/* Body */}
          <div className="px-8 py-8 flex flex-col items-center gap-5">
            <p className="text-sm text-gray-500 font-work-sans text-center leading-relaxed">
              Sign in with your JM Test Systems Microsoft account to access payroll,
              expenses, KPI tracking, and intelligent 1:1 meeting tools.
            </p>

            {error && (
              <div className="w-full flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-700 font-work-sans">{error}</p>
              </div>
            )}

            <button
              onClick={() => { void handleLogin(); }}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-jm-yellow text-jm-dark font-montserrat font-semibold rounded-xl hover:bg-jm-yellow-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-jm-navy border-t-transparent rounded-full animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  {/* Microsoft logo icon */}
                  <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                  </svg>
                  <span>Sign in with Microsoft</span>
                </>
              )}
            </button>

            <p className="text-xs text-gray-400 font-work-sans text-center">
              Use your <strong>@jmtest.com</strong> Microsoft account
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-white/40 font-work-sans mt-6">
          &copy; {new Date().getFullYear()} JM Test Systems. All rights reserved.
        </p>
      </div>
    </div>
  );
}
